import i18n from '@/localisation/i18n';

/**
 * Fallback UI для глобального ErrorBoundary.
 *
 * Раньше любая необработанная ошибка рендера приводила к белому экрану
 * (React размонтировал всё дерево). Теперь пользователь видит сообщение
 * с кнопкой перезагрузки. Отправку ошибки в Sentry берёт на себя сам
 * Sentry.ErrorBoundary, поэтому тут captureException не дублируем.
 *
 * Используем синхронный экземпляр i18n (как в остальном коде), а не хук
 * useTranslation: этот компонент рендерится при краше React-дерева, когда
 * хук-контекст может быть недоступен.
 */
export const ErrorFallback = ({ resetError }: { resetError: () => void }) => {
  const reload = () => {
    const url = new URL(window.location.href);
    url.searchParams.set('_r', String(Date.now()));

    // Если ошибка вызвана битым/устаревшим кэшем Service Worker (например,
    // SW отдаёт удалённые хэшированные ассеты), обычная перезагрузка не
    // поможет — SW снова перехватит навигацию. Поэтому сначала принудительно
    // unregister'им все регистрации SW.
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker
        .getRegistrations()
        .then(registrations =>
          Promise.all(registrations.map(reg => reg.unregister()))
        )
        .catch(() => {})
        .finally(() => {
          window.location.replace(url.toString());
        });
    } else {
      window.location.replace(url.toString());
    }
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 16,
        padding: 24,
        textAlign: 'center',
        background: '#1F1F1F',
        color: '#fff',
        fontFamily:
          'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
      }}
    >
      <h1 style={{ fontSize: 18, fontWeight: 700, margin: 0 }}>
        {i18n.t('error.title')}
      </h1>
      <p style={{ fontSize: 14, opacity: 0.7, margin: 0, maxWidth: 360 }}>
        {i18n.t('error.description')}
      </p>
      <button
        onClick={() => {
          resetError();
          reload();
        }}
        style={{
          marginTop: 8,
          padding: '12px 24px',
          borderRadius: 10,
          border: 'none',
          background: '#6854FF',
          color: '#fff',
          fontSize: 14,
          fontWeight: 600,
          cursor: 'pointer',
        }}
      >
        {i18n.t('error.reload')}
      </button>
    </div>
  );
};
